import React, { useEffect, useState } from 'react';
import { Observer } from 'mobx-react';
import { Link, useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { Typography, Button, Tooltip } from '@material-ui/core';
import { css } from '@emotion/core';
import { toJS } from 'mobx';
import CirclePlusIcon from '@material-ui/icons/AddCircleOutline';
import FilterIcon from '@material-ui/icons/FilterList';
import OpenInNew from '@material-ui/icons/OpenInNew';
import {
  useQueryParams,
  NumberParam,
  StringParam,
  withDefault,
  JsonParam,
} from 'use-query-params';

import { useRootStore } from '../../../stores/BridgeProvider';
import storageService from '../../../services/storage.service';
import Spinner from '../../util/Spinner';
import { FlexDiv, WideBox } from '../../styles/styles';
import { dateSort, voucherStatusMapping } from '../../util/TableUtils';
import { CustomTable, FilterPlaceholder } from '../../common/CustomTable';
import { VoucherTypeIdEnumsShort } from '../../common/Enums';
import { NumberHandler } from '../../util/NumberHandler';
import { isCompareObjects } from '../../util/UtilFunctions';
import VouchersJoyride from '../../joyrides/VouchersJoyride';
import RenderPropModal from '../../dealMapper/common/RenderPropModal';
import FilterModal from './FilterModal';

const actionableContainer = css`
  display: flex;
  span {
    display: flex;
  }
  a {
    & :last-child {
      display: block;
      margin-left: 15px;
    }
  }
`;

const columns = [
  {
    Header: 'Voucher #',
    accessor: 'VoucherNumber',
    id: 'voucherNumber',
    Filter: FilterPlaceholder,
  },
  {
    Header: 'Type',
    accessor: 'VoucherTypeCode',
    Cell: ({ row }) => {
      const {
        original: { VoucherTypeDesc, VoucherTypeCode },
      } = row;

      return (
        <Tooltip
          title={VoucherTypeDesc}
          placement="left"
          enterDelay={300}
          leaveDelay={200}
        >
          <div style={{ width: '1rem', margin: '0 auto' }}>
            {VoucherTypeCode}
          </div>
        </Tooltip>
      );
    },
    id: 'voucherTypeCode',
    Filter: FilterPlaceholder,
    width: 50,
  },
  {
    Header: 'Contact',
    accessor: 'ContactName',
    id: 'contactName',
    width: 120,
  },
  {
    Header: 'Date',
    accessor: d => format(d.VoucherDate, 'MM/DD/YYYY'),
    id: 'date',
    sortType: dateSort,
    width: 80,
  },
  {
    Header: 'Due Date',
    accessor: d => (d.DueDate ? format(d.DueDate, 'MM/DD/YYYY') : 'N/A'),
    id: 'dueDate',
    sortType: dateSort,
    width: 80,
  },
  {
    Header: 'Total',
    Cell: ({ row }) =>
      row.original.Amount ? (
        <NumberHandler
          num={row.original.Amount}
          float={2}
          justify="flex-end"
          currency="$"
        />
      ) : (
        'N/A'
      ),
    accessor: d => (d.Amount ? d.Amount : null),
    id: 'total',
    width: 60,
  },
  {
    Header: 'Status',
    accessor: d => voucherStatusMapping[d.StatusId],
    id: 'status',
    width: 90,
  },
  {
    // FIXME: what should be displayed?
    Header: 'Mapped',
    accessor: 'IsMapped',
    id: 'mapped',
    width: 50,
  },
  {
    Header: 'Amount Mapped',
    Cell: ({ row }) =>
      row.original.AmountMapped ? (
        <NumberHandler
          num={row.original.AmountMapped}
          float={2}
          justify="flex-end"
          currency="$"
        />
      ) : (
        'N/A'
      ),
    accessor: d => (d.AmountMapped ? d.AmountMapped : null),
    id: 'amountMapped',
    width: 80,
  },
  {
    Header: 'Amount Due',
    Cell: ({ row }) =>
      row.original.AmountPaid ? (
        <NumberHandler
          num={row.original.Amount - row.original.AmountPaid}
          float={2}
          justify="flex-end"
          currency="$"
        />
      ) : (
        'N/A'
      ),
    accessor: d => d.AmountPaid || null,
    width: 70,
    id: 'amountDue',
  },
  {
    width: 40,
    Cell: ({ row }) => (
      <a
        onClick={e => e.stopPropagation()}
        href={`/vouchers/${row.original.VoucherId}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          color: '#666',
        }}
      >
        <OpenInNew />
      </a>
    ),
    id: 'newTab',
  },
];

const Vouchers = () => {
  const [, setQuery] = useQueryParams({
    page: NumberParam,
    rows: NumberParam,
    search: withDefault(StringParam, ''),
    sort: withDefault(JsonParam, []),
  });

  const defaultFilters = storageService.getItem('voucherFilters');
  const { dataStore } = useRootStore();
  const history = useHistory();

  const [isLoading, setLoading] = useState(true);
  const [filters, setFilters] = useState(defaultFilters || {});

  const vouchers = Array.isArray(dataStore.voucherArray)
    ? dataStore.voucherArray[0]
    : [];
  const rowInfo = Array.isArray(dataStore.voucherArray)
    ? dataStore.voucherArray[1]
    : [];

  const fetchFilteredVouchers = async payload => {
    const filterJson = {
      DateFilter1Type: '>',
      DateFilter2Type: '<',
    };

    if (payload.recordLimit) filterJson.RecordLimit = +payload.recordLimit;
    if (payload.dateFilter1) filterJson.DateFilter1 = payload.dateFilter1;
    if (payload.dateFilter2) filterJson.DateFilter2 = payload.dateFilter2;
    if (payload.voucherTypeCode)
      filterJson.VoucherTypeCode =
        VoucherTypeIdEnumsShort[payload.voucherTypeCode];
    if (payload.status) filterJson.Status = payload.status;
    if (payload.contact) filterJson.Contact = payload.contact;

    await dataStore.retrieveFilteredVouchers(JSON.stringify(filterJson));
  };

  useEffect(() => {
    const initialize = async () => {
      await fetchFilteredVouchers(filters);

      setLoading(false);
    };
    initialize();
  }, []);

  const onChangeFilter = async updatedFilters => {
    setQuery({ page: 1 });

    setLoading(true);
    setFilters(updatedFilters);
    storageService.setItem('voucherFilters', updatedFilters);
    await fetchFilteredVouchers(updatedFilters);
    setLoading(false);
  };

  if (isLoading) return <Spinner />;

  const visibleVouchers = toJS(vouchers).filter(v => !v.IsHidden);

  return (
    <Observer
      render={() => {
        return (
          <FlexDiv>
            <WideBox style={{ maxWidth: '1500px', width: '98%' }}>
              <CustomTable
                data={visibleVouchers}
                columns={columns}
                minRows={1}
                trClassName="react-table-hover-row"
                getTrProps={({ row }) => {
                  return {
                    // TODO: className is no longer a thing here
                    className: row && 'react-table-hover-row',
                    onClick: () =>
                      history.push(`/vouchers/${row.original.VoucherId}`),
                  };
                }}
                tableHeader={
                  <Typography variant="h2" id="vouchersStep1">
                    Transactions
                  </Typography>
                }
                rowInfo={rowInfo[0]}
                actionables={
                  <div css={actionableContainer}>
                    <RenderPropModal
                      manageHandleOpen
                      cardDisplay={({ handleClose }) => (
                        <FilterModal
                          filters={filters}
                          handleClose={handleClose}
                          onChangeFilter={onChangeFilter}
                        />
                      )}
                    >
                      {() => {
                        const foundRows = rowInfo[0]?.FoundRows || 0;
                        const totalRows = rowInfo[0]?.TotalRows || 0;
                        const hasFilters =
                          !isCompareObjects({}, filters) ||
                          foundRows < totalRows;

                        return (
                          <Button
                            style={{
                              marginRight: '10px',
                            }}
                            variant="outlined"
                            color={hasFilters ? 'primary' : 'default'}
                          >
                            <FilterIcon
                              style={{ marginRight: '4px', marginLeft: '-3px' }}
                            />
                            {`Filters${hasFilters ? ' (Active)' : ''}`}
                          </Button>
                        );
                      }}
                    </RenderPropModal>
                    <Button
                      id="vouchersStep2"
                      variant="outlined"
                      color="primary"
                      component={Link}
                      to={{
                        pathname: '/vouchers/new',
                        state: { voucherType: 'paySomeone' },
                      }}
                    >
                      <CirclePlusIcon
                        style={{ marginRight: '4px', marginLeft: '-3px' }}
                      />
                      Pay Someone
                    </Button>
                    <Button
                      id="vouchersStep3"
                      variant="outlined"
                      color="primary"
                      component={Link}
                      to={{
                        pathname: '/vouchers/new',
                        state: { voucherType: 'collectPayment' },
                      }}
                    >
                      <CirclePlusIcon
                        style={{ marginRight: '4px', marginLeft: '-3px' }}
                      />
                      Collect Payment
                    </Button>
                  </div>
                }
                containerStyle={{ padding: '0 2rem' }}
              />
            </WideBox>
            <VouchersJoyride />
          </FlexDiv>
        );
      }}
    />
  );
};

export default Vouchers;
